import React, { useEffect, useState } from "react";
import { company, services, sellingPoints, faqs } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CheckCircle, Building2, Link2, ShoppingCart, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { formatPhoneFR } from "../utils/phone";
import { Footer } from "../components/Footer";

const GlassCard = ({ children, className = "" }) => (
  <div className={`glass-card ${className}`}>{children}</div>
);

const Pill = ({ children }) => (
  <span className="px-3 py-1 text-sm" style={{
    background: "var(--brand-hover)",
    color: "var(--brand-primary)",
    border: "1px solid var(--border-subtle)",
  }}>{children}</span>
);

export default function Landing() {
  useEffect(() => {
    const reveals = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add("in-view");
      })
    }, { threshold: 0.15 });
    reveals.forEach(r => io.observe(r));
    return () => io.disconnect();
  }, []);

  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      const submissions = JSON.parse(localStorage.getItem("cominvest_contact") || "[]");
      const payload = { ...form, ts: new Date().toISOString() };
      localStorage.setItem("cominvest_contact", JSON.stringify([payload, ...submissions]));
      toast.success("Merci, votre demande a bien été envoyée (simulation)");
      setForm({ name: "", phone: "", message: "" });
      const contactSec = document.getElementById("contact");
      if (contactSec) contactSec.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    }
  };

  return (
    <main className="dark-full-container">
      {/* HERO */}
      <section id="accueil" className="dark-container">
        <div className="dark-content-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center pt-20">
            <div className="reveal">
              <h1 className="display-huge mb-6">Conseil & B2B à impact pour les entreprises</h1>
              <p className="body-medium mb-8" style={{maxWidth: 640}}>
                {company.name} accompagne dirigeants, PME et grands comptes à {company.city} et partout en France: conseil en gestion, apport d’affaires qualifié et gestion de centrales d’achat non-alimentaire.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Pill>Conseil opérationnel</Pill>
                <Pill>Mise en relation décideurs</Pill>
                <Pill>Centrales d’achat</Pill>
              </div>
              <div className="flex gap-3">
                <a href={`tel:${company.phone}`} className="btn-primary dark-button-animate"><Phone size={16}/> {formatPhoneFR(company.phone)}</a>
                <a href="#contact" className="btn-secondary dark-button-animate">Demander un rappel</a>
              </div>
            </div>
            <div className="relative reveal">
              <div className="hero-blob" aria-hidden />
              <GlassCard className="p-6">
                <div className="grid grid-cols-3 gap-4">
                  {sellingPoints.map((s, i) => (
                    <div key={i} className="text-center">
                      <div className="display-medium" style={{color: "var(--brand-primary)"}}>{s.kpi}</div>
                      <div className="body-small">{s.label}</div>
                    </div>
                  ))}
                </div>
              </GlassCard>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <GlassCard className="p-5">
                  <div className="flex items-center gap-3 mb-2"><Building2 size={18} color="var(--brand-primary)"/><span className="heading-3">Conseil</span></div>
                  <p className="body-small">Pilotage, organisation, KPI. Objectif: performance durable.</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <div className="flex items-center gap-3 mb-2"><Link2 size={18} color="var(--brand-primary)"/><span className="heading-3">Apport B2B</span></div>
                  <p className="body-small">Réseau de décideurs, opportunités qualifiées, suivi.</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <div className="flex items-center gap-3 mb-2"><ShoppingCart size={18} color="var(--brand-primary)"/><span className="heading-3">Centrales</span></div>
                  <p className="body-small">Achats non-alimentaires mutualisés, économies d’échelle.</p>
                </GlassCard>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="pad-large" style={{background: "var(--bg-primary)"}}>
        <div className="dark-content-container">
          <h2 className="display-large mb-6">Nos services</h2>
          <div className="dark-grid">
            {services.map((s, idx) => (
              <GlassCard key={idx} className="p-6 reveal">
                <h3 className="heading-1 mb-2">{s.title}</h3>
                <p className="body-medium mb-4">{s.description}</p>
                <ul className="list-none space-y-2">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 body-small"><CheckCircle size={18} color="var(--brand-primary)"/> {b}</li>
                  ))}
                </ul>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* APPORT */}
      <section id="apport" className="pad-large" style={{background: "var(--bg-primary)"}}>
        <div className="dark-content-container">
          <h2 className="display-large mb-6">Apporteur d’affaires</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="reveal">
              <p className="body-medium mb-4">Nous orchestrons des mises en relation B2B ciblées entre décideurs qui partagent un besoin réel et actuel.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["Ciblage & qualification","Mise en relation contextualisée","Suivi pipeline & feedback","Transparence contractuelle"].map((t, i)=> (
                  <GlassCard key={i} className="p-4"><span className="body-medium">{t}</span></GlassCard>
                ))}
              </div>
            </div>
            <div className="reveal">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem value={`item-${i}`} key={i}>
                    <AccordionTrigger className="heading-3">{f.q}</AccordionTrigger>
                    <AccordionContent className="body-small">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CENTRALES */}
      <section id="centrales" className="pad-large" style={{background: "var(--bg-primary)"}}>
        <div className="dark-content-container">
          <h2 className="display-large mb-6">Centrales d’achat</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 reveal">
            {["Analyse des besoins","Sourcing & AO","Négociation & contrats","Pilotage & KPI"].map((step, i) => (
              <div key={i} className="p-5 step-card">
                <div className="display-medium" style={{color: "var(--brand-primary)"}}>0{i+1}</div>
                <div className="heading-3 mb-2">{step}</div>
                <div className="body-small">Approche structurée pour capturer des économies durables.</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="pad-large" style={{background: "var(--bg-primary)"}}>
        <div className="dark-content-container grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="reveal">
            <h2 className="display-large mb-2">Contact</h2>
            <p className="body-medium mb-6">Parlez-nous de votre besoin. Réponse sous 24h ouvrées.</p>
            <form onSubmit={handleSubmit} className="space-y-4 glass-card p-6">
              <div>
                <label className="body-small mb-1 block">Nom</label>
                <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} required placeholder="Votre nom"/>
              </div>
              <div>
                <label className="body-small mb-1 block">Téléphone</label>
                <Input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} placeholder="Numéro"/>
              </div>
              <div>
                <label className="body-small mb-1 block">Message</label>
                <Textarea rows={5} value={form.message} onChange={e=>setForm({...form, message: e.target.value})} placeholder="Dites-nous en plus..."/>
              </div>
              <Button type="submit" className="btn-primary w-full flex items-center justify-center gap-2"><Phone size={16}/> Demander un rappel</Button>
            </form>
          </div>
          <div className="reveal">
            <div className="glass-card p-6 space-y-3">
              <div className="flex items-center gap-2"><MapPin size={18} color="var(--brand-primary)"/> <span className="body-medium">{company.address}</span></div>
              <div className="flex items-center gap-2"><Phone size={18} color="var(--brand-primary)"/> <a className="body-medium" href={`tel:${company.phone}`}>{formatPhoneFR(company.phone)}</a></div>
            </div>
            <div className="cta-banner mt-6">
              <div>
                <div className="heading-1">Prêt à démarrer ?</div>
              </div>
              <a href={`tel:${company.phone}`} className="btn-primary"><Phone size={16}/> {formatPhoneFR(company.phone)}</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
